import React, {Fragment} from "react";
import {ChecklistItem} from "@reside/reside-api-admission";
import {FieldError} from "@reside/ui";

import {usePccDocumentUploadModalContext} from "../pcc-document-upload-modal";
import {useDashboardAdmissionsContext} from "../../DashboardAdmissionsContext";
import {admissionHasActivePccResidentAccountStatus} from "../../../../services/PccService";
import {AdmissionFromSearch} from "../../../../services/AdmissionsService";
import {PccDocumentUploadTaskCheckbox} from "../pcc-document-upload-task-checkbox";
import {DisabledNotApplicableStatusOption} from "../cell/admission-checklist/DisableNotApplicableStatusOption";
import {
  ChecklistItemDateHoverSlot,
  ChecklistItemTextColumn,
  Label as AdmissionChecklistRowLabel,
} from "../cell/admission-checklist/style";
import {
  TaskCheckboxChildren,
  TaskCheckboxFadedSmall,
  TaskCheckboxContextProvider,
  TASK_CHECKBOX_BOX_PX,
} from "../../../task-checkbox-field";

export const ApprovalChecklist = ({
  checklist,
  admission,
}: {
  admission: AdmissionFromSearch;
  checklist: ReadonlyArray<ChecklistItem>;
}) => {
  const {facilityFlagsById} = useDashboardAdmissionsContext();
  const {isPccDashboardCheckboxesFeatureEnabled, openPccUploadModal} =
    usePccDocumentUploadModalContext();

  const onUpload = (checklistItem: ChecklistItem) =>
    openPccUploadModal({
      admission,
      checklistItem,
    });

  const {facilityHasPccTurnedOn, facilityHasPccUploadActive} =
    facilityFlagsById?.[admission.facilityId] || {};

  const isPccDocumentUploadEnabled =
    isPccDashboardCheckboxesFeatureEnabled &&
    facilityHasPccTurnedOn &&
    facilityHasPccUploadActive &&
    admissionHasActivePccResidentAccountStatus(admission);

  return (
    <TaskCheckboxContextProvider>
      {checklist.map(checklistItem => (
        <Fragment key={checklistItem.id}>
          <TaskCheckboxFadedSmall
            name={checklistItem.id}
            label={checklistItem.description}
            tooltipLength={63}
            checkboxBoxPx={TASK_CHECKBOX_BOX_PX.large}
            Label={AdmissionChecklistRowLabel}
            renderTrailingWithCaret={({FormattedDate}) => (
              <ChecklistItemDateHoverSlot
                data-checklist-item-date-hover=""
                size="medium"
              >
                <FormattedDate />
              </ChecklistItemDateHoverSlot>
            )}
            renderNotApplicableStatusOption={
              isPccDocumentUploadEnabled && checklistItem.documentUploadRequired
                ? DisabledNotApplicableStatusOption
                : undefined
            }
            onComplete={
              isPccDocumentUploadEnabled && checklistItem.documentUploadRequired
                ? ({setCheckboxClosed}) => {
                    setCheckboxClosed();
                    onUpload(checklistItem);
                  }
                : undefined
            }
          >
            {isPccDocumentUploadEnabled
              ? (props: TaskCheckboxChildren) => (
                  <PccDocumentUploadTaskCheckbox
                    {...props}
                    checklistItem={checklistItem}
                    onUpload={onUpload}
                  />
                )
              : ({
                  Checkbox,
                  Label: LabelSlot,
                }: TaskCheckboxChildren) => (
                  <>
                    <Checkbox />
                    <ChecklistItemTextColumn>
                      <LabelSlot />
                    </ChecklistItemTextColumn>
                  </>
                )}
          </TaskCheckboxFadedSmall>
          <FieldError name={`${checklistItem.id}.checked`} />
        </Fragment>
      ))}
    </TaskCheckboxContextProvider>
  );
};
